import React from "react";
import { DragDropProvider, DragOverlay } from "@dnd-kit/react";
import { useSearchParams } from "react-router-dom";
import { BillsPayment } from "@pages/BillsPage/components/BillsPayment/BillsPayment";
import { UnpaidPreviousPeriodBillWarning } from "@pages/BillsPage/components/UnpaidPreviousPeriodBillWarning/UnpaidPreviousPeriodBillWarning";
import { useJointBillServicePeriod } from "@pages/BillsPage/useJointBillServicePeriod";
import { BannerMessages } from "@features/BannerMessages/BannerMessages";
import { ChubbInsuranceNotification } from "@features/ChubbInsuranceNotification/ChubbInsuranceNotification";
import { EduInstitutionNotification } from "@features/EduInstitutionNotification/EduInstitutionNotification";
import { FilloutKycWithAntiMoneyLaunderingFormNotification } from "@features/FilloutKycWithAntiMoneyLaunderingFormNotification/FilloutKycWithAntiMoneyLaunderingFormNotification";
import { GjensidigeInsuranceNotification } from "@features/GjensidigeInsuranceNotification/GjensidigeInsuranceNotification";
import { JointBillEmptyHouseMessage } from "@features/JointBillEmptyHouseMessage/JointBillEmptyHouseMessage";
import { JointBillPeriod } from "@features/JointBillList/components/JointBillPeriod/JointBillPeriod";
import { KnowYourCustomerNotification } from "@features/KnowYourCustomerNotification/KnowYourCustomerNotification";
import { PaymentRestrictionNotification } from "@features/PaymentRestrictionNotification/PaymentRestrictionNotification";
import { JointBillTableSkeleton } from "@features/skeletons/Bills/JointBillTableSkeleton/JointBillTableSkeleton";
import { useFeatureConfig } from "@hooks/useFeatureFlags";
import { useValidJointBillServicesForPeriod } from "@hooks/useValidJointBillServicesForPeriod";
import {
  useJointBillServicePeriodDataQueries,
  useOverviewQuery,
} from "@services/overview/queries";
import { SelectionState } from "@services/overview/types";
import { isNullOrUndefined } from "@utils/common";
import { COUNTRY_CODE } from "@/constants";
import { CountryCode } from "@/i18n";

export const JointBillList: React.FC = () => {
  const { periodId = 0, isLoading: isPeriodIdLoading } =
    useJointBillServicePeriod();
  const { isChubbInsuranceEnabled } = useFeatureConfig();
  const validJbsForPeriod = useValidJointBillServicesForPeriod(periodId);
  const [searchParams] = useSearchParams();
  const requestedJointBillServiceId = parseInt(searchParams.get("jbsId") ?? "");
  const { data: overview, isLoading: isOverviewLoading } = useOverviewQuery();
  const isLoading = isOverviewLoading || isPeriodIdLoading;
  const requestedJointBillService = requestedJointBillServiceId
    ? overview?.jointBillServices?.find(
        (jbs) => jbs.jointBillServiceId === requestedJointBillServiceId,
      )
    : null;

  const jointBillServices = (
    !isNullOrUndefined(requestedJointBillService)
      ? [requestedJointBillService]
      : overview?.jointBillServices
  )
    ?.filter((jbs) => validJbsForPeriod.includes(jbs.jointBillServiceId))
    .sort((a, b) => a.sortOrder - b.sortOrder);

  const jointBillPeriodQueries = useJointBillServicePeriodDataQueries(
    (jointBillServices ?? []).map((jbs) => ({
      jointBillServiceId: jbs.jointBillServiceId,
      periodId,
    })),
  );

  const mainJointBillPeriodData = jointBillPeriodQueries.find(
    (query) =>
      query.data?.jointBillServiceId ===
      jointBillServices?.[0]?.jointBillServiceId,
  )?.data;

  const canAnyPeriodBePaid = jointBillPeriodQueries.some(
    (query) => !!query.data?.canBePaid,
  );

  const selectedAndCanBePaidJbsIds = jointBillPeriodQueries
    .filter(
      (query) =>
        !!query.data?.canBePaid &&
        query.data?.selectionState !== SelectionState.NOT_SELECTED,
    )
    .map((query) => query.data?.jointBillServiceId ?? 0);

  const isPeriodDataLoading = jointBillPeriodQueries.some(
    (query) => query.isLoading,
  );

  if (isLoading) {
    return (
      <div className="mx-0 flex flex-col gap-10">
        <JointBillTableSkeleton />
      </div>
    );
  }

  return !jointBillServices?.length ? (
    <JointBillEmptyHouseMessage
      isFilterProblem={!isNullOrUndefined(requestedJointBillService)}
    />
  ) : (
    <div className="flex flex-col gap-4">
      <div className="ml-4 w-[calc(100dvw-2rem)] overflow-x-auto scrollbar-hide md:ml-0 md:w-full">
        <div className="flex gap-4 md:flex-col [&>*]:min-w-[80vw] [&>*]:md:min-w-[unset]">
          <UnpaidPreviousPeriodBillWarning />
          <KnowYourCustomerNotification />
          <PaymentRestrictionNotification />
          <FilloutKycWithAntiMoneyLaunderingFormNotification />
          <EduInstitutionNotification />
          <GjensidigeInsuranceNotification />
          {isChubbInsuranceEnabled &&
            !isPeriodDataLoading &&
            COUNTRY_CODE === CountryCode.PL &&
            mainJointBillPeriodData?.insuranceStatus !== "active" && (
              <ChubbInsuranceNotification
                variant={mainJointBillPeriodData?.insuranceStatus}
                jointBillServiceId={mainJointBillPeriodData?.jointBillServiceId}
              />
            )}
          <BannerMessages />
        </div>
      </div>
      <div>
        <div className="mx-0 flex flex-col gap-10">
          <DragDropProvider>
            {jointBillServices.map((jointBillService, index) => (
              <JointBillPeriod
                orderId={index}
                key={jointBillService.jointBillServiceId}
                jointBillService={jointBillService}
                periodId={periodId}
              />
            ))}
            <DragOverlay>
              <div></div>
            </DragOverlay>
          </DragDropProvider>
        </div>
        {!isPeriodDataLoading && canAnyPeriodBePaid && (
          <BillsPayment
            periodId={periodId}
            selectedJointBillServiceIds={selectedAndCanBePaidJbsIds}
          />
        )}
      </div>
    </div>
  );
};
